<script>
  import { fade } from 'svelte/transition';
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { get } from 'svelte/store';
  import { _ } from 'svelte-i18n';
  import fetchData from '../api.js';
  import Header from '../components/Header.svelte';
  import TopInfo from '../components/TopInfo.svelte';
  import UserStore from '../stores/user';

  let settings = {
    max_bins_per_wave: 50,
    warehouse_locations: '',
  };
  let bins = [];
  let draftBarcodes = {};
  let assignedBinLabels = {};
  let responseMessage = '';
  let responseError = '';

  function t(key, values = {}) {
    return get(_)(key, { values });
  }

  async function request(action, payload = {}) {
    let formData = {
      token: $UserStore?.data,
      action,
      ...payload,
    };

    return fetchData(formData);
  }

  function buildBinCode(labelNumber) {
    return `BIN-${String(labelNumber).padStart(3, '0')}`;
  }

  function parseLocations(value) {
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  async function ensureBinsInventory(targetCount) {
    const desiredCount = Math.max(1, Number(targetCount) || 50);
    const existingByLabel = new Set(
      (Array.isArray(bins) ? bins : []).map((bin) => Number(bin.label_number)).filter((num) => Number.isInteger(num) && num > 0),
    );

    const missingLabels = [];
    for (let label = 1; label <= desiredCount; label += 1) {
      if (!existingByLabel.has(label)) {
        missingLabels.push(label);
      }
    }

    if (!missingLabels.length) {
      return { ok: true, created: 0 };
    }

    let created = 0;
    for (const labelNumber of missingLabels) {
      const createResult = await request('bins_create', {
        bin_code: buildBinCode(labelNumber),
        label_number: labelNumber,
      });

      if (createResult?.success) {
        created += 1;
      }
    }

    return { ok: true, created };
  }

  async function refresh() {
    const [settingsResult, binsResult] = await Promise.all([
      request('bins_settings_get'),
      request('bins_list'),
    ]);

    if (settingsResult?.success && settingsResult?.settings) {
      const locations = String(settingsResult.settings.warehouse_locations || '').trim();

      settings = {
        max_bins_per_wave: Number(settingsResult.settings.max_bins_per_wave) || 50,
        warehouse_locations: locations,
      };
    }

    bins = binsResult?.success && Array.isArray(binsResult.bins)
      ? binsResult.bins
      : [];

    await ensureBinsInventory(settings.max_bins_per_wave);

    const finalBinsResult = await request('bins_list');
    bins = finalBinsResult?.success && Array.isArray(finalBinsResult.bins)
      ? finalBinsResult.bins
      : bins;

    bins = bins
      .slice()
      .sort((a, b) => Number(a?.label_number || 0) - Number(b?.label_number || 0))
      .slice(0, Math.max(1, Number(settings.max_bins_per_wave) || 50));

    draftBarcodes = bins.reduce((acc, bin) => {
      acc[bin.label_number] = bin.bin_code || '';
      return acc;
    }, {});
  }

  async function saveSettings() {
    const parsedLocations = parseLocations(settings.warehouse_locations);
    const normalizedLocations = parsedLocations.join(',');

    settings.warehouse_locations = normalizedLocations;

    const result = await request('bins_settings_update', {
      max_bins_per_wave: String(settings.max_bins_per_wave),
      warehouse_locations: normalizedLocations,
    });

    if (!result?.success) {
      responseError = result?.error || 'Failed to save settings.';
      responseMessage = '';
      await refresh();
      return;
    }

    await ensureBinsInventory(settings.max_bins_per_wave);

    responseMessage = t('bins.messages.settings_saved');
    responseError = '';
    await refresh();
  }

  async function saveBinAssignment(labelNumber, barcode) {
    const targetBin = bins.find((item) => Number(item.label_number) === Number(labelNumber));
    if (!targetBin?.bin_code) {
      responseError = 'Bin not found.';
      responseMessage = '';
      return;
    }

    const normalizedBarcode = String(barcode || '').trim();
    if (!normalizedBarcode) {
      responseError = 'Barcode is required.';
      responseMessage = '';
      return;
    }

    const result = await request('bins_barcode_assign', {
      bin_code: targetBin.bin_code,
      barcode: normalizedBarcode,
    });

    if (!result?.success) {
      responseError = result?.error || 'Failed to assign barcode.';
      responseMessage = '';
      await refresh();
      return;
    }

    responseMessage = t('bins.messages.bin_assigned', {
      number: labelNumber,
      barcode: normalizedBarcode,
    });
    assignedBinLabels = {
      ...assignedBinLabels,
      [Number(labelNumber)]: true,
    };
    responseError = '';
    await refresh();
  }

  onMount(() => {
    if (!$UserStore || !$UserStore.data) {
      push('/login');
      return;
    }

    refresh();
  });
</script>

<div class="page-container" transition:fade={{ delay: 250, duration: 300 }}>
  <Header activeLink="shipments" />
  <div class="page">
    <TopInfo title={$_('bins.title')} />
    <div class="shipment">

    <div class="bg-white rounded-xl shadow p-4 mb-4">
      <div class="font-bold mb-3">{$_('bins.settings.title')}</div>
      <div class="grid md:grid-cols-2 gap-3">
        <div class="flex gap-2 items-center flex-wrap">
        <label for="maxBinsInput">{$_('bins.settings.max_bins_per_wave')}</label>
        <input id="maxBinsInput" type="number" min="1" bind:value={settings.max_bins_per_wave} class="border rounded px-2 py-1" />
        </div>

        <div class="flex gap-2 items-center flex-wrap">
          <label for="warehouseLocationsInput">Warehouse Locations</label>
          <input
            id="warehouseLocationsInput"
            type="text"
            bind:value={settings.warehouse_locations}
            placeholder="27,22,14"
            class="border rounded px-2 py-1 min-w-[220px]"
          />
        </div>

        <div class="flex gap-2 items-center flex-wrap md:col-span-2">
          <button on:click={saveSettings} class="bg-red-600 text-white rounded px-3 py-1">{$_('bins.common.save')}</button>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl shadow p-4 mb-4">
      <div class="font-bold mb-3">{$_('bins.assignment.title')}</div>
      <div class="text-sm text-slate-600 mb-3">
        {$_('bins.assignment.active_bins', { values: { count: bins.length } })}
      </div>
      <div class="overflow-auto max-h-[420px] border rounded">
        <table class="w-full text-sm">
          <thead class="bg-slate-50 sticky top-0">
            <tr class="border-b">
              <th class="text-left py-2 px-2 w-[140px]">{$_('bins.assignment.bin_number')}</th>
              <th class="text-left py-2 px-2">{$_('bins.assignment.barcode')}</th>
              <th class="text-left py-2 px-2 w-[140px]">{$_('bins.assignment.action')}</th>
            </tr>
          </thead>
          <tbody>
            {#each bins as bin}
              <tr class={`border-b ${assignedBinLabels[Number(bin.label_number)] ? 'bg-emerald-50' : ''}`}>
                <td class="py-2 px-2 font-bold">
                  #{bin.label_number}
                  {#if assignedBinLabels[Number(bin.label_number)]}
                    <span class="ml-2 inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold">
                      {$_('bins.assignment.badges.barcode_assigned')}
                    </span>
                  {/if}
                </td>
                <td class="py-2 px-2">
                  <input
                    class="border rounded px-2 py-1 w-full"
                    bind:value={draftBarcodes[bin.label_number]}
                    on:input={() => {
                      assignedBinLabels = {
                        ...assignedBinLabels,
                        [Number(bin.label_number)]: false,
                      };
                    }}
                    placeholder={$_('bins.assignment.scan_barcode')}
                  />
                </td>
                <td class="py-2 px-2">
                  <button
                    on:click={() => saveBinAssignment(bin.label_number, draftBarcodes[bin.label_number])}
                    class="bg-red-600 text-white rounded px-2 py-1"
                  >
                    {$_('bins.common.save')}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    {#if responseMessage}
      <div class="mt-2 text-emerald-700">{responseMessage}</div>
    {/if}
    {#if responseError}
      <div class="mt-2 text-red-700">{responseError}</div>
    {/if}
    </div>
  </div>
</div>
